import { LayoutContext } from '@context/LayoutContext';
import ArrowDoodle from '@monofront/design-system/visuals/Illustrations/ArrowDoodle';
import { useContext } from 'react';
import { RichText } from 'prismic-reactjs';
import CustomPrismicHeading from '@components/CustomPrismicHeading';
import withSlice from '@screens/SlicesContainer/withSlice';
import { mapStepsWithAnchors } from './mapStepsWithAnchors';
import { IStepsWithAnchors } from './types';

const StepsWithAnchors: React.FC<IStepsWithAnchors> = ({ items, primary }) => {
  const { mainHeaderHeight } = useContext(LayoutContext);

  if (!primary || !items || !items.length) return null;

  const goToAnchor = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();

    const anchorId = e.currentTarget.getAttribute('href') || '';
    const anchorElement = document.querySelector(anchorId);

    if (!anchorElement) return;

    const top =
      anchorElement.getBoundingClientRect().top +
      window.scrollY -
      (mainHeaderHeight || 0);

    window.scrollTo({ top, behavior: 'smooth' });
  };

  return (
    <>
      <section className="steps-with-anchors">
        {primary?.title?.length ? (
          <div className="main-title-container">
            <RichText
              render={primary.title}
              htmlSerializer={CustomPrismicHeading({
                className: 'catchy-title main-title',
              })}
            />
          </div>
        ) : null}
        <ol>
          {items.map((step, i) => (
            <li key={i}>
              <div className="step">
                <p className="accent-title">{step.title}</p>
                <p className="step-description">{step.description}</p>
                {step.anchorLabel && step.anchorTargetId && (
                  <a
                    href={`#slice-${step.anchorTargetId}`}
                    className="tw-lf-link-md tw-mt-3 tw-inline-block"
                    onClick={goToAnchor}
                  >
                    {step.anchorLabel}
                  </a>
                )}
              </div>
              <ArrowDoodle
                aria-hidden="true"
                UNSAFE_className="step-arrow"
                flipHorizontally={i % 2 !== 0}
              />
            </li>
          ))}
        </ol>
      </section>
      <style jsx>{`
        ol {
          display: flex;
          list-style: none;
          counter-reset: anchor-step-counter;
        }
        ol li {
          position: relative;
          flex-grow: 1;
          list-style: none;
          padding: 0 var(--space-5);
          counter-increment: anchor-step-counter;
        }

        ol li :global(.step-arrow) {
          position: absolute;
          right: -15px;
          top: 40%;
        }
        ol li:last-child :global(.step-arrow) {
          display: none;
        }

        .main-title-container {
          text-align: center;
          padding-bottom: var(--space-6);
        }
        .step {
          display: flex;
          flex-direction: column;
          height: 100%;
          max-width: 192px;
          margin: 0 auto;
          text-align: center;
        }
        .step:before {
          content: counter(anchor-step-counter);
          display: inline-block;
          align-self: center;
          margin-bottom: var(--space-4);
          border: 3px solid ${primary.accentColor || `var(--clr-primary-1)`};
          border-radius: 53% 56% 45% 50% / 53% 59% 44% 54%;
          width: 25px;
          height: 25px;
          line-height: 28px;
          font-family: var(--font-family-1);
          font-size: 18px;
        }
        .step .accent-title {
          margin-bottom: var(--space-2);
        }
        .step .step-description {
          margin-bottom: auto;
        }

        @media (max-width: 800px) {
          .steps-with-anchors {
            margin: var(--negative-padding-layout-base);
          }
          ol {
            overflow: auto;
          }
          ol::-webkit-scrollbar {
            display: none;
          }
          ol li {
            padding: 0 var(--space-7);
          }
          .step {
            width: 167px;
          }
        }
      `}</style>
    </>
  );
};

export default withSlice(StepsWithAnchors, mapStepsWithAnchors);
